import {
  Box,
  useTheme,
  TextField,
  Divider,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  Stack,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useRef, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import { TIMEZONE_OPTIONS, DEFAULT_TIMEZONE, COUNTRY_OPTIONS } from "constants/CONST";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBoxRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import Switch from "@mui/material/Switch";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { HtmlTooltip } from "components/HeatMap";
import { CustomPopper } from "components/CustomPopper";
import { useAuth } from "../../../contexts/AuthContext";
import api from "services/api";
import { countryOptions } from "constants/countryData";
import { usePageTitle } from "hooks/usePageTitle";
import { useWebSocket } from "contexts/WebSocketContext";
import { parsePhoneNumber } from "libphonenumber-js";
import Overlay from "components/Overlay/Overlay";

// Helper: format a phone number based on country code (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const phoneNumber = parsePhoneNumber(raw, countryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const toNullableBoolean = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  return null;
};

const getInitialRequirementValues = (linguist, requirementDefinitions = []) => {
  const requirementMap = { ...(linguist?.RequirementMap || {}) };
  const requirements = linguist?.Requirements || linguist?.RequirementValues || [];

  requirements.forEach((requirement) => {
    const sourceColumn = requirement?.Definition?.SourceColumn || requirement?.SourceColumn;
    if (sourceColumn) {
      requirementMap[sourceColumn] = toNullableBoolean(requirement.Value);
    }
  });

  const values = {};
  requirementDefinitions.forEach(({ SourceColumn }) => {
    const fallback = toNullableBoolean(linguist?.[SourceColumn]);
    values[SourceColumn] =
      typeof requirementMap[SourceColumn] !== "undefined"
        ? toNullableBoolean(requirementMap[SourceColumn])
        : fallback;
  });

  return values;
};

const getRequirementDefinitionsForForm = (
  linguist,
  dynamicDefinitions = [],
) => {
  const mergedDefinitions = new Map();

  dynamicDefinitions.forEach((definition) => {
    const sourceColumn = definition?.SourceColumn;
    if (!sourceColumn) return;

    mergedDefinitions.set(sourceColumn, {
      Key: definition?.Key || sourceColumn,
      Name: definition?.Name || sourceColumn,
      SourceColumn: sourceColumn,
    });
  });

  const requirements = linguist?.Requirements || linguist?.RequirementValues || [];
  requirements.forEach((requirement) => {
    const sourceColumn =
      requirement?.Definition?.SourceColumn || requirement?.SourceColumn;
    if (!sourceColumn) return;

    const name = requirement?.Definition?.Name || requirement?.Name || sourceColumn;
    const key = requirement?.Definition?.Key || requirement?.Key || sourceColumn;

    if (!mergedDefinitions.has(sourceColumn)) {
      mergedDefinitions.set(sourceColumn, {
        Key: key,
        Name: name,
        SourceColumn: sourceColumn,
      });
    }
  });

  return Array.from(mergedDefinitions.values()).sort((left, right) =>
    String(left?.Name || "").localeCompare(String(right?.Name || "")),
  );
};

// Per-mode linguist rates + per-language Simultaneous capability (locked
// design 2026-10-06; capability addendum: thrall-simult-capability-design;
// OFF-shape revision 2026-10-06: the simult field is ALWAYS rendered and is
// disabled + zeroed when OFF, superseding the earlier "hide when OFF").
// Rates are stored as up to two LinguistLanguage rows per language:
// Mode 'Consecutive' (base) + Mode 'Simultaneous', or a single Mode 'All'
// row when both rates match. The form keeps ONE entry per language with
// { Rate, SimultaneousRate, CanHandleSimultaneous, SimultaneousTouched }.
// Capability is DERIVED from row shapes (no schema change). A language with
// SimultaneousTouched=false and capability ON is ATTACHED: its simult field
// mirrors base-rate edits live (follow-until-touched) until the user edits
// it; a capability OFF->ON toggle re-attaches and re-prefills from the base.

// LOAD mapping (DB rows -> form entries), tolerant of legacy shapes:
//   'All' / legacy null        -> ON,  Rate = row.Rate, simult attached (= base)
//   'Consecutive' (alone)      -> OFF, Rate = row.Rate, simult = 0 (disabled)
//   'Simultaneous' (alone)     -> ON,  Rate = '', simult = row.Rate, detached
//   Consecutive + Simultaneous -> ON,  both values from their rows, detached
const buildLinguistLanguageEntries = (linguistLanguages = []) => {
  const byLanguage = new Map();

  (linguistLanguages || []).forEach((rawLing) => {
    const languageId = rawLing?.Language?.id ?? rawLing?.LanguageID;
    if (languageId === undefined || languageId === null) return;

    const key = String(languageId);
    if (!byLanguage.has(key)) {
      byLanguage.set(key, {
        id: languageId,
        Name: rawLing?.Language?.Name ?? rawLing?.Name ?? "",
        baseRate: null,
        simultRate: null,
        allRate: null,
      });
    }

    const entry = byLanguage.get(key);
    const languageName = rawLing?.Language?.Name ?? rawLing?.Name;
    if (!entry.Name && languageName) {
      entry.Name = languageName;
    }

    const mode = rawLing?.Mode ?? "All";
    const rate = rawLing?.Rate ?? "";

    if (mode === "Simultaneous") {
      entry.simultRate = rate;
    } else if (mode === "Consecutive") {
      entry.baseRate = rate;
    } else if (entry.allRate === null) {
      // 'All' (incl. legacy NULL Mode) carries the both-modes rate.
      entry.allRate = rate;
    }
  });

  return Array.from(byLanguage.values()).map((entry) => {
    const language = {
      id: entry.id,
      Name: entry.Name,
      Rate: "",
      SimultaneousRate: "",
      CanHandleSimultaneous: false,
      SimultaneousTouched: false,
    };

    if (entry.simultRate !== null) {
      // Simultaneous row present (alone or paired with a Consecutive row):
      // capability ON; base only when a Consecutive row provides one; the
      // pair / simult-only shapes load DETACHED (explicit stored rates).
      language.CanHandleSimultaneous = true;
      language.SimultaneousRate = entry.simultRate;
      language.SimultaneousTouched = true;
      if (entry.baseRate !== null) {
        language.Rate = entry.baseRate;
      }
    } else if (entry.baseRate !== null) {
      // Consecutive row alone: canonical "cannot do Simultaneous" shape;
      // the visible-but-disabled simult field shows its OFF placeholder 0.
      language.Rate = entry.baseRate;
      language.SimultaneousRate = 0;
    } else if (entry.allRate !== null) {
      // 'All' / legacy NULL: ON at the same rate, simult attached to base.
      language.CanHandleSimultaneous = true;
      language.Rate = entry.allRate;
      language.SimultaneousRate = entry.allRate;
    }

    return language;
  });
};

// SAVE serialization (form entries -> payload rows) - frozen table
// (capability addendum supersedes the earlier table):
//   capability OFF         -> ONE row  'Consecutive',  Rate = base (stale/0 simult ignored)
//   ON + simult empty      -> ONE row  'All',          Rate = base
//   ON + base empty        -> ONE row  'Simultaneous', Rate = simult
//   ON + both set & equal  -> ONE row  'All',          Rate = base
//   ON + both set & differ -> TWO rows 'Consecutive' (base) + 'Simultaneous' (simult)
// `id` = LanguageID keeps the historical payload convention; LanguageID is
// the authoritative field for the server's (LanguageID, Mode) composite key.
const serializeLinguistLanguages = (languages = []) => {
  const rows = [];

  (languages || []).forEach((language) => {
    if (language?.id === undefined || language?.id === null) return;

    const baseRate = language.Rate ?? "";
    const simultRate = language.SimultaneousRate ?? "";
    const hasBase = String(baseRate).trim() !== "";
    // A zero simult rate is the capability-OFF placeholder: treat it exactly
    // like blank so it never flips the equal-vs-differ decision or creates a
    // 'Simultaneous' row with Rate 0. hasBase is intentionally unchanged
    // (a written 0 base keeps its previous meaning).
    const hasSimult =
      String(simultRate).trim() !== "" && Number(simultRate) !== 0;

    const makeRow = (Mode, Rate) => ({
      id: language.id,
      LanguageID: language.id,
      Rate,
      Mode,
    });

    if (!language.CanHandleSimultaneous) {
      // Incapable: base-only row (a stale simult value is ignored).
      rows.push(makeRow("Consecutive", baseRate));
    } else if (!hasSimult) {
      rows.push(makeRow("All", baseRate));
    } else if (!hasBase) {
      rows.push(makeRow("Simultaneous", simultRate));
    } else if (Number(baseRate) === Number(simultRate)) {
      rows.push(makeRow("All", baseRate));
    } else {
      rows.push(makeRow("Consecutive", baseRate));
      rows.push(makeRow("Simultaneous", simultRate));
    }
  });

  return rows;
};

// A language entry is save-ready when at least one usable rate is set
// (0 / '' / null count as unset, matching the previous save-gate semantics).
// Capability OFF only counts the base rate; ON accepts either rate.
const hasLanguageRate = (language) => {
  if (!language) return false;
  if (!language.CanHandleSimultaneous) return Boolean(language.Rate);
  return Boolean(language.Rate) || Boolean(language.SimultaneousRate);
};

// ATTACHED = capability ON and the user has not edited the simult field for
// that language yet; attached fields mirror base-rate edits live.
const isSimultaneousAttached = (language) =>
  Boolean(language?.CanHandleSimultaneous) && !language?.SimultaneousTouched;

// Follow-until-touched: mirror a base-rate edit into the simult field, but
// only while the field is still attached.
const applyBaseRateMirror = (language, baseRate) =>
  isSimultaneousAttached(language)
    ? { ...language, SimultaneousRate: baseRate }
    : language;

// Apply a base-rate edit to one language entry: set Rate, then mirror into
// the attached simult field. Used by per-language base fields, the shared
// "Rate For All Languages" field, and the single/multiple switch.
const applyBaseRate = (language, baseRate) =>
  applyBaseRateMirror({ ...language, Rate: baseRate }, baseRate);

// Capability toggle:
//   OFF -> the simult field stays visible but DISABLED and zeroed (0 shown);
//          forget its value + detached state.
//   ON  -> re-attach and re-prefill from the CURRENT base rate.
const applySimultaneousCapability = (language, enabled) =>
  enabled
    ? {
        ...language,
        CanHandleSimultaneous: true,
        SimultaneousTouched: false,
        SimultaneousRate: language?.Rate ?? "",
      }
    : {
        ...language,
        CanHandleSimultaneous: false,
        SimultaneousTouched: false,
        SimultaneousRate: 0,
      };

const LinguistNewEdit = () => {
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  // Rates table (2026-10-06): the grid table starts at md (900px). Below
  // that the per-language rows stack and keep their inline labels.
  const isMdUp = useMediaQuery("(min-width:900px)");
  const { overlayData, setOverlayData, setOverlayName, triggerPageRefresh } =
    useStateContext();
  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? "New Linguist"
      : "Edit Linguist",
  );

  const linguistLanguages =
    overlayData.Linguist &&
    buildLinguistLanguageEntries(overlayData.Linguist.LinguistLanguages);

  const [requirementDefinitions, setRequirementDefinitions] = useState(() =>
    getRequirementDefinitionsForForm(overlayData.Linguist, []),
  );

  const [selectedLanguages, setSelectedLanguages] = useState(
    linguistLanguages || [],
  );

  const [allDialects, setAllDialects] = useState([]);
  // Flat array of selected dialect objects: [{ id, Name, LanguageID }, ...]
  const [selectedDialects, setSelectedDialects] = useState(() => {
    const dialects = overlayData?.Linguist?.Dialects || [];
    return dialects
      .filter((d) => d.Dialect)
      .map((d) => ({
        id: d.DialectID,
        Name: d.Dialect.Name,
        LanguageID: d.Dialect.Language?.id || d.Dialect.LanguageID,
      }));
  });

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [rateError, setRateError] = useState(false);

  const [useMultipleRates, setUseMultipleRates] = useState(
    linguistLanguages ? linguistLanguages.length > 1 : false,
  );

  const [rate, setRate] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : linguistLanguages && linguistLanguages.length > 0
        ? linguistLanguages[0].Rate || ""
        : "",
  );

  // Track original rate to detect changes
  const [originalRate] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? null
      : linguistLanguages && linguistLanguages.length > 0
        ? linguistLanguages[0].Rate || null
        : null,
  );

  const [minimumHours, setMinimumHours] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? 1
      : overlayData.Linguist
        ? overlayData.Linguist.MinimumHours
        : 1,
  );

  const isAvailableInPerson =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailableInPerson
        : false;

  const isAvailableVirtual =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailableVirtual
        : false;

  const isAvailablePhone =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.LinguistLanguages
        ? overlayData.Linguist.IsAvailablePhone
        : false;

  const [availableTypes, setAvailableTypes] = useState([
    {
      Name: "Phone",
      IsAvailable: isAvailablePhone,
    },
    {
      Name: "Virtual",
      IsAvailable: isAvailableVirtual,
    },
    {
      Name: "In Person",
      IsAvailable: isAvailableInPerson,
    },
  ]);

  const updateAvailability = (name, isAvailable) => {
    setAvailableTypes((prevTypes) =>
      prevTypes.map((type) =>
        type.Name === name ? { ...type, IsAvailable: isAvailable } : type,
      ),
    );
  };

  const [name, setName] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Name,
  );
  const [email, setEmail] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Email,
  );

  const initialAddress =
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? { line1: "", line2: "", line3: "" }
      : {
          line1: overlayData.Linguist.AddressLine1 || "",
          line2: overlayData.Linguist.AddressLine2 || "",
          line3: overlayData.Linguist.AddressLine3 || "",
        };

  const [addressLine1, setAddressLine1] = useState(initialAddress.line1);
  const [addressLine2, setAddressLine2] = useState(initialAddress.line2);
  const [addressLine3, setAddressLine3] = useState(initialAddress.line3);

  const [country, setCountry] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Country || "",
  );

  const initPhoneNumbers = () => {
    if (
      overlayData.Action !== OVERLAY_NAMES.LINGUIST_ACTION_NEW &&
      overlayData.Linguist.PhoneNumbers &&
      overlayData.Linguist.PhoneNumbers.length > 0
    ) {
      return overlayData.Linguist.PhoneNumbers.map((p, idx) => {
        // Find the country code from the stored calling code
        const matchedCountry = countryOptions.find(
          (c) => String(c.callingCode) === String(p.CountryCode),
        );
        const countryCode = matchedCountry?.code || "US";
        const rawNumber = p.Number || "";
        return {
          id: p.id || `existing-${idx}`,
          countryCode,
          callingCode: String(p.CountryCode || "1"),
          number: rawNumber ? formatPhoneNumber(rawNumber, countryCode) : "",
          ext: p.Ext || "",
          label: p.Label || "",
          isPrimary: p.IsPrimary || false,
          sortOrder: p.SortOrder || idx,
        };
      });
    }
    return [
      {
        id: "new-0",
        countryCode: "US",
        callingCode: "1",
        number: "",
        ext: "",
        label: "",
        isPrimary: true,
        sortOrder: 0,
      },
    ];
  };

  const [phoneNumbers, setPhoneNumbers] = useState(initPhoneNumbers);

  const [status, setStatus] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Status,
  );

  const [certification, setCertification] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? "N/A"
      : overlayData.Linguist.Certifications,
  );

  const [active, setActive] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? false
      : overlayData.Linguist.Active || false,
  );
  const [linguistTimezone, setLinguistTimezone] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? DEFAULT_TIMEZONE
      : overlayData.Linguist.Timezone || DEFAULT_TIMEZONE,
  );
  const selectedTimezone =
    String(linguistTimezone || "").trim() || DEFAULT_TIMEZONE;
  const timezoneOptions =
    TIMEZONE_OPTIONS.some((option) => option.value === selectedTimezone)
      ? TIMEZONE_OPTIONS
      : [
          ...TIMEZONE_OPTIONS,
          { value: selectedTimezone, label: selectedTimezone },
        ];
  const [requirementValues, setRequirementValues] = useState(() =>
    getInitialRequirementValues(
      overlayData.Linguist,
      getRequirementDefinitionsForForm(overlayData.Linguist, []),
    ),
  );

  // Track user's manual toggles so they aren't overwritten by sync
  const userTogglesRef = useRef({});

  // Track which requirements were just refreshed (↻) in this session
  // so the switch visually shows the refresh position
  const refreshedRef = useRef({});

  const handleRequirementToggle = (sourceColumn, checked) => {
    userTogglesRef.current[sourceColumn] = checked;
    setRequirementValues((previous) => ({
      ...previous,
      [sourceColumn]: checked,
    }));
  };

  const handleMarkUpdated = async (sourceColumn) => {
    if (!overlayData?.LinguistID || !isEditable) return;
    try {
      await api.patch(
        `/linguists/${overlayData.LinguistID}/requirements/${sourceColumn}/mark-updated`,
      );
      // Mark as refreshed so the switch visually moves to the ↻ position
      refreshedRef.current[sourceColumn] = true;
      // Clear any user toggle override for this column so the sync effect
      // picks up the correct value from the refreshed DB data
      delete userTogglesRef.current[sourceColumn];
      // Refresh linguist data so the history dialog shows the new verifiedAt
      const refreshed = await api.get(`/linguists/${overlayData.LinguistID}`);
      if (refreshed?.data) {
        setOverlayData((prev) => ({
          ...prev,
          Linguist: refreshed.data,
        }));
      }
    } catch (err) {
      console.error("Failed to mark requirement as updated:", err);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const syncRequirementDefinitions = async () => {
      try {
        const response = await api.get("/linguists/requirements");
        if (!isMounted) return;

        const fromDb = Array.isArray(response?.data) ? response.data : [];
        const mergedDefinitions = getRequirementDefinitionsForForm(
          overlayData.Linguist,
          fromDb,
        );

        setRequirementDefinitions(mergedDefinitions);
        setRequirementValues((previous) => {
          const initialValues = getInitialRequirementValues(
            overlayData.Linguist,
            mergedDefinitions,
          );
          const nextValues = { ...initialValues };

          // Preserve user's manual toggles (they take priority)
          Object.keys(userTogglesRef.current).forEach((sourceColumn) => {
            if (Object.prototype.hasOwnProperty.call(nextValues, sourceColumn)) {
              nextValues[sourceColumn] = userTogglesRef.current[sourceColumn];
            }
          });

          // Also preserve any previous values not in userToggles
          Object.keys(previous || {}).forEach((sourceColumn) => {
            if (Object.prototype.hasOwnProperty.call(nextValues, sourceColumn)
                && !(sourceColumn in userTogglesRef.current)) {
              nextValues[sourceColumn] = previous[sourceColumn];
            }
          });

          return nextValues;
        });
      } catch (error) {
        if (!isMounted) return;

        const fallbackDefinitions = getRequirementDefinitionsForForm(
          overlayData.Linguist,
          [],
        );
        setRequirementDefinitions(fallbackDefinitions);
        setRequirementValues((previous) => {
          const nextValues = getInitialRequirementValues(overlayData.Linguist, fallbackDefinitions);
          // Preserve user toggles on fallback too
          Object.keys(userTogglesRef.current).forEach((sourceColumn) => {
            if (Object.prototype.hasOwnProperty.call(nextValues, sourceColumn)) {
              nextValues[sourceColumn] = userTogglesRef.current[sourceColumn];
            }
          });
          return nextValues;
        });
      }
    };

    syncRequirementDefinitions();

    return () => {
      isMounted = false;
    };
  }, [overlayData.Linguist]);

  useEffect(() => {
    let isMounted = true;

    const fetchDialects = async () => {
      try {
        const dialectsResponse = await api.get("/linguists/dialects");
        if (isMounted && dialectsResponse?.data) {
          setAllDialects(dialectsResponse.data);

          // If editing, also fetch the linguist's selected dialects now that we have allDialects
          if (overlayData.Action !== OVERLAY_NAMES.LINGUIST_ACTION_NEW) {
            const linguistId = overlayData?.Linguist?.id;
            if (linguistId) {
              try {
                const linguistResponse = await api.get(`/linguists/${linguistId}`);
                if (isMounted && linguistResponse?.data) {
                  const dialects = linguistResponse.data.Dialects || [];
                  const mapped = dialects
                    .filter((d) => d.Dialect)
                    .map((d) => ({
                      id: d.DialectID,
                      Name: d.Dialect.Name,
                      LanguageID: d.Dialect.Language?.id || d.Dialect.LanguageID,
                    }));
                  setSelectedDialects(mapped);
                }
              } catch (err) {
                // Silently fail
              }
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          setAllDialects([]);
        }
      }
    };

    fetchDialects();

    return () => {
      isMounted = false;
    };
  }, [overlayData.Action, overlayData?.Linguist?.id]);

  useEffect(() => {
    let isMounted = true;

    const loadLinguistTimezone = async () => {
      if (overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW) {
        setLinguistTimezone(DEFAULT_TIMEZONE);
        return;
      }

      const linguistId = overlayData?.Linguist?.id;
      if (!linguistId) {
        setLinguistTimezone(DEFAULT_TIMEZONE);
        return;
      }

      try {
        const response = await api.get(
          `/linguists/${linguistId}/notification-preferences`,
        );

        if (!isMounted) return;

        const timezone = String(response?.data?.Timezone || "").trim();
        setLinguistTimezone(timezone || DEFAULT_TIMEZONE);
      } catch (error) {
        if (!isMounted) return;
        setLinguistTimezone(DEFAULT_TIMEZONE);
      }
    };

    loadLinguistTimezone();

    return () => {
      isMounted = false;
    };
  }, [overlayData.Action, overlayData.Linguist]);



  const [, setCertificationOther] = useState(
    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? ""
      : overlayData.Linguist.Certifications || "",
  );

  const otherCertificationRef = useRef(null);
  const languagesRef = useRef(null);

  const saveLinguistTimezonePreference = async (linguistId) => {
    if (!linguistId) return;

    await api.put(`/linguists/${linguistId}/notification-preferences`, {
      Timezone:
        String(linguistTimezone || "").trim() || DEFAULT_TIMEZONE,
    });
  };

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    BrowserNotificationsEnabled: true,
    SmsNotificationsEnabled: false,
    QuietHoursStart: "22:00",
    QuietHoursEnd: "07:00",
  });
  const [, setIsSavingNotificationSettings] = useState(false);

  const fetchNotificationPreferences = useCallback(async () => {
    if (overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW) return;
    const linguistId = overlayData?.Linguist?.id;
    if (!linguistId) return;

    try {
      const response = await api.get(
        `/linguists/${linguistId}/notification-preferences`,
      );
      if (response?.data) {
        setNotificationSettings((prev) => ({
          ...prev,
          ...response.data,
        }));
      }
    } catch (error) {
      // Silently fail — notification preferences are optional
    }
  }, [overlayData.Action, overlayData?.Linguist?.id]);

  const saveNotificationPreferences = async (linguistId) => {
    if (!linguistId) return;

    setIsSavingNotificationSettings(true);
    try {
      await api.put(`/linguists/${linguistId}/notification-preferences`, {
        BrowserNotificationsEnabled: notificationSettings.BrowserNotificationsEnabled,
        SmsNotificationsEnabled: notificationSettings.SmsNotificationsEnabled,
        QuietHoursStart: notificationSettings.QuietHoursStart,
        QuietHoursEnd: notificationSettings.QuietHoursEnd,
        Timezone: String(linguistTimezone || "").trim() || DEFAULT_TIMEZONE,
      });
    } catch (error) {
      // Silently fail — notification preferences are optional
    } finally {
      setIsSavingNotificationSettings(false);
    }
  };

  // Load notification preferences when editing an existing linguist
  useEffect(() => {
    fetchNotificationPreferences();
  }, [fetchNotificationPreferences]);

  function saveChanges() {
    // Validation
    if (!name || name.trim() === "") {
      setNameError(true);
      alert("Please enter a linguist name.");
      return;
    }

    if (!email || email.trim() === "") {
      setEmailError(true);
      alert("Please enter an email address.");
      return;
    }

    // Reset errors
    setNameError(false);
    setEmailError(false);

    setIsSavingChanges(true);
    setIsEditable(false);

    // Build requirements array from definitions (fallback to RequirementMap if definitions not yet loaded)
    const requirementsFromDefinitions = requirementDefinitions.map((definition) => ({
      ...definition,
      Value: toNullableBoolean(requirementValues[definition.SourceColumn]),
    }));

    // If definitions haven't loaded yet, build from the linguist's existing RequirementMap
    const requirementsFromMap = [];
    if (requirementsFromDefinitions.length === 0 && overlayData.Linguist?.RequirementMap) {
      const allDefinitions = overlayData.Linguist.Requirements || [];
      allDefinitions.forEach((req) => {
        const sourceColumn = req?.Definition?.SourceColumn || req?.SourceColumn;
        if (sourceColumn && req?.Definition) {
          requirementsFromMap.push({
            Key: req.Definition.Key || sourceColumn,
            Name: req.Definition.Name || sourceColumn,
            SourceColumn: sourceColumn,
            RequirementDefinitionID: req.RequirementDefinitionID,
            Value: toNullableBoolean(requirementValues[sourceColumn] ?? req.Value),
          });
        }
      });
    }

    const requirements = requirementsFromDefinitions.length > 0
      ? requirementsFromDefinitions
      : requirementsFromMap;

    const data = {
      LinguistID: overlayData.LinguistID,
      Linguist: {
        Name: name,
        Email: email,
        Address: [addressLine1, addressLine2, addressLine3]
          .map((part) => (typeof part === "string" ? part.trim() : ""))
          .filter(Boolean)
          .join(", "),
        AddressLine1: addressLine1?.trim() || null,
        AddressLine2: addressLine2?.trim() || null,
        AddressLine3: addressLine3?.trim() || null,
        Country: country?.trim() || null,
        PhoneNumber: phoneNumbers[0]?.number || null,
        Status: status || null,
        Certifications: certification,
        MinimumHours: minimumHours || 1,
        IsAvailablePhone: availableTypes[0].IsAvailable,
        IsAvailableVirtual: availableTypes[1].IsAvailable,
        IsAvailableInPerson: availableTypes[2].IsAvailable,
        Active: active,
        Timezone: selectedTimezone,
        Requirements: requirements,
      },

      Requirements: requirements,

      LinguistLanguages: serializeLinguistLanguages(selectedLanguages),

      LinguistDialects: selectedDialects.map((dialect) => ({
        id: dialect.id,
      })),

      PhoneNumbers: phoneNumbers
        .filter((p) => p.number && p.number.trim())
        .map((p, idx) => {
          // Strip formatting before saving — keep only digits
          const rawNumber = p.number.replace(/\D/g, "");
          // Strip the calling code prefix from the number if present
          const callingCode = String(p.callingCode || "1");
          let cleanNumber = rawNumber;
          if (callingCode !== "1" && cleanNumber.startsWith(callingCode)) {
            cleanNumber = cleanNumber.slice(callingCode.length);
          } else if (callingCode === "1" && cleanNumber.length > 10 && cleanNumber.startsWith("1")) {
            cleanNumber = cleanNumber.slice(1);
          }
          return {
            ...(p.id && !String(p.id).startsWith("new-") ? { id: Number(p.id) || p.id } : {}),
            CountryCode: p.callingCode || "1",
            Number: cleanNumber,
            Ext: p.ext ? p.ext.trim() : null,
            Label: p.label ? p.label.trim() : null,
            IsPrimary: p.isPrimary || idx === 0,
            SortOrder: p.sortOrder || idx,
          };
        }),

      MemberID: currentUser.id,
    };

    overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
      ? addNewLinguist(data)
      : updateLinguist(data);
  }

  function addNewLinguist(data) {
    api
      .post("/linguists/addNewLinguist", {
        data: data,
      })
      .then(async (response) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        if (response.status === 201 || response.data) {
          const createdLinguistId =
            response?.data?.linguist?.id || response?.data?.id || null;
          let timezoneSaveError = null;

          if (createdLinguistId) {
            try {
              await saveLinguistTimezonePreference(createdLinguistId);
            } catch (error) {
              timezoneSaveError = error;
            }
            try {
              await saveNotificationPreferences(createdLinguistId);
            } catch (error) {
              // Notification preferences are non-critical
            }
          }

          triggerPageRefresh("New Linguist Added Successfully!", theme);

          if (timezoneSaveError) {
            alert(
              "Linguist was saved, but timezone could not be updated. Please retry from the linguist profile.",
            );
          }

          // Emit socket events to notify other users
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Linguist",
              recordId: createdLinguistId || response.data.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: response?.data?.linguist?.Name || response.data.Name,
            });

            // Also notify the linguists list page to refetch
            socket.emit("assignment:saved", {
              modelType: "LinguistsList",
              recordId: "all",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: response?.data?.linguist?.Name || response.data.Name,
            });
          }

          setOverlayName("");
        }
      })
      .catch((err) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to add linguist. Please try again.";
        const detail = err.response?.data?.detail;
        alert(`Error: ${errorMessage}${detail ? `\n\nDetail: ${detail}` : ""}`);
      });
  }

  function updateLinguist(data) {
    api
      .post("/linguists/updateLinguist", {
        data: data,
      })
      .then(async (response) => {
        setIsSavingChanges(false);
        setIsEditable(true);

        if (response.status === 201 || response.data) {
          let timezoneSaveError = null;
          try {
            await saveLinguistTimezonePreference(overlayData?.Linguist?.id);
          } catch (error) {
            timezoneSaveError = error;
          }
          try {
            await saveNotificationPreferences(overlayData?.Linguist?.id);
          } catch (error) {
            // Notification preferences are non-critical
          }

          triggerPageRefresh("Linguist Updated Successfully!", theme);

          if (timezoneSaveError) {
            alert(
              "Linguist was updated, but timezone could not be updated. Please retry from the linguist profile.",
            );
          }

          // Emit socket events to notify other users
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Linguist",
              recordId: overlayData.Linguist.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.Linguist.Name,
            });

            // Also notify the linguists list page to refetch
            socket.emit("assignment:saved", {
              modelType: "LinguistsList",
              recordId: "all",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.Linguist.Name,
            });
          }

          // Check if rate has changed and automatically open rate update overlay.
          // Two independent signals:
          //  1. The "Rate For All Languages" field changed (single-rate mode).
          //  2. ANY per-language rate changed (multiple-rates mode) — compare
          //     each language's form rate against its original loaded rate.
          // Both signals are keyed on the BASE (consecutive) rate only. A
          // change to the simultaneous rate alone deliberately does NOT open
          // this overlay this release (updateFutureRates is out of scope for
          // the per-mode rate rollout).
          const currentRate = parseFloat(rate || 0);
          const oldRate = parseFloat(originalRate || 0);
          const allLanguagesRateChanged = Math.abs(currentRate - oldRate) > 0.01;

          const originalLanguages = linguistLanguages || [];
          const changedRates = (selectedLanguages || [])
            .map((lang) => {
              const original = originalLanguages.find(
                (ol) => ol.id === lang.id,
              );
              if (!original) return null;
              const origRate = parseFloat(original.Rate || 0);
              const newLangRate = parseFloat(lang.Rate || 0);
              if (Math.abs(newLangRate - origRate) > 0.01) {
                return {
                  languageId: lang.id,
                  languageName: lang.Name,
                  newRate: lang.Rate,
                  oldRate: original.Rate,
                };
              }
              return null;
            })
            .filter(Boolean);

          const rateChanged =
            allLanguagesRateChanged || changedRates.length > 0;

          if (rateChanged) {
            // Rate has changed - open the rate update overlay
            const rateUpdateData = {
              LinguistID: overlayData.Linguist.id,
              LinguistName: name,
              NewRate: rate,
              IsAllLanguagesRate: allLanguagesRateChanged,
              RateUpdates: changedRates,
            };
            setOverlayData(rateUpdateData);
            setOverlayName(OVERLAY_NAMES.LINGUIST_RATE_UPDATE);
          } else {
            // No rate change - just close the overlay
            setOverlayName("");
          }
        }
      })
      .catch((err) => {
        console.error("Error updating linguist:", err);
        setIsSavingChanges(false);
        setIsEditable(true);

        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to update linguist. Please try again.";
        const detail = err.response?.data?.detail;
        alert(`Error: ${errorMessage}${detail ? `\n\nDetail: ${detail}` : ""}`);
      });
  }

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
      }}
    >
      {isMobile ? (
        <Tooltip
          title="Discard"
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[4],
                border: `1px solid ${theme.palette.divider}`,
                fontSize: "0.75rem",
              },
            },
            arrow: { sx: { color: theme.palette.background.paper } },
          }}
        >
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip
          title="Save"
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[4],
                border: `1px solid ${theme.palette.divider}`,
                fontSize: "0.75rem",
              },
            },
            arrow: { sx: { color: theme.palette.background.paper } },
          }}
        >
          <span>
            <IconButton
              disabled={
                !useMultipleRates
                  ? !rate && !selectedLanguages.some(hasLanguageRate)
                  : selectedLanguages.length === 0 ||
                    !selectedLanguages.every(hasLanguageRate)
              }
              color="primary"
              onClick={saveChanges}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor:
                    theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          disabled={
            !useMultipleRates
              ? !rate && !selectedLanguages.some(hasLanguageRate)
              : selectedLanguages.length === 0 ||
                !selectedLanguages.every(hasLanguageRate)
          }
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          onClick={saveChanges}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <Overlay
      id="linguist-new-edit"
      open={true}
      onClose={() => setOverlayName("")}
      title={
        overlayData.Action === OVERLAY_NAMES.LINGUIST_ACTION_NEW
          ? "New Linguist"
          : "Edit Linguist"
      }
      footer={footer}
    >
      <Box
        sx={{
          flexGrow: 1,
          p: "1rem 2.3rem 1rem 2.5rem",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
          <Grid container spacing={2}>
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h4" fontWeight="bold">
                Account Information
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!isEditable}
                    checked={active}
                    onChange={(event) => setActive(event.target.checked)}
                  />
                }
                label="Active"
                labelPlacement="start"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Name"
                label="Name"
                //defaultValue={name || "N/A"}
                error={nameError}
                value={name || ""}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value.trim() === "");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="Email"
                label="Email"
                //defaultValue={email || "N/A"}
                error={emailError}
                value={email || ""}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setEmailError(event.target.value.trim() === "");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine1"
                label="Address Line 1"
                error={addressError}
                value={addressLine1 || ""}
                onChange={(event) => {
                  setAddressLine1(event.target.value);
                  setAddressError(event.target.value.trim() === "");
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine2"
                label="Address Line 2 (Optional)"
                value={addressLine2 || ""}
                onChange={(event) => {
                  setAddressLine2(event.target.value);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id="AddressLine3"
                label="City, State ZIP"
                value={addressLine3 || ""}
                onChange={(event) => {
                  setAddressLine3(event.target.value);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="Country"
                label="Country of Origin"
                value={country || ""}
                onChange={(event) => {
                  setCountry(event.target.value);
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 300 },
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select a country</em>
                </MenuItem>
                {COUNTRY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="LinguistStatus"
                label="Linguist Status"
                //defaultValue={status || "N/A"}
                value={status || ""}
                onChange={(event) => {
                  setStatus(event.target.value);
                }}
              >
                {overlayData.LinguistStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                //multiline
                id="MinHours"
                label="Minimum Hours"
                //defaultValue={notes || "N/A"}
                value={minimumHours || 1}
                onChange={(event) => {
                  setMinimumHours(event.target.value);
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id="LinguistTimezone"
                label="Timezone"
                value={selectedTimezone}
                onChange={(event) => {
                  setLinguistTimezone(event.target.value);
                }}
              >
                {timezoneOptions.map((timezoneOption) => (
                  <MenuItem
                    key={timezoneOption.value}
                    value={timezoneOption.value}
                  >
                    {timezoneOption.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            {/* Phone Numbers Section */}
            <Grid container size={{ xs: 12 }}>
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  Phone Numbers
                </Typography>
                <IconButton
                  disabled={!isEditable}
                  size="small"
                  onClick={() => {
                    setPhoneNumbers((prev) => [
                      ...prev,
                      {
                        id: `new-${Date.now()}`,
                        countryCode: prev[prev.length - 1]?.countryCode || "US",
                        callingCode: prev[prev.length - 1]?.callingCode || "1",
                        number: "",
                        ext: "",
                        label: "",
                        isPrimary: false,
                        sortOrder: prev.length,
                      },
                    ]);
                  }}
                  sx={{
                    "&.Mui-disabled": { opacity: 0.5 },
                  }}
                >
                  <AddRoundedIcon fontSize="small" />
                </IconButton>
              </Grid>

              {phoneNumbers.map((phone, index) => (
                <Grid
                  container
                  key={phone.id}
                  spacing={1}
                  sx={{
                    mb: index < phoneNumbers.length - 1 ? 1.5 : 0,
                    width: "100%",
                    ml: 0,
                  }}
                >
                  <Grid size={{ xs: 12, sm: 3, md: 4 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      label="Code"
                      value={phone.countryCode || "US"}
                      onChange={(event) => {
                        const selectedCode = event.target.value;
                        const country = countryOptions.find(
                          (c) => c.code === selectedCode,
                        );
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index
                              ? {
                                  ...p,
                                  countryCode: selectedCode,
                                  callingCode: country
                                    ? String(country.callingCode)
                                    : p.callingCode,
                                }
                              : p,
                          ),
                        );
                      }}
                      fullWidth
                      SelectProps={{
                        renderValue: (selCode) => {
                          const c = countryOptions.find(
                            (o) => o.code === selCode,
                          );
                          if (!c) return selCode;
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <img
                                src={c.flagUrl}
                                alt={c.name}
                                style={{ width: 18, height: "auto", borderRadius: 2 }}
                              />
                              <span>{c.name}</span>
                              <span style={{ color: "#999", fontSize: 12 }}>+{c.callingCode}</span>
                            </span>
                          );
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: { maxHeight: 350 },
                          },
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                        },
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              width: "100%",
                            }}
                          >
                            <img
                              src={country.flagUrl}
                              alt={country.name}
                              style={{ width: 24, height: "auto", borderRadius: 2, flexShrink: 0 }}
                              loading="lazy"
                            />
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {country.name}
                            </span>
                            <span style={{ color: "#999", fontSize: 13, flexShrink: 0 }}>
                              +{country.callingCode}
                            </span>
                          </Box>
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3, md: 3 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Phone Number"
                      error={phoneNumberError && index === 0}
                      value={
                        phone._phoneRaw !== undefined
                          ? phone._phoneRaw
                          : phone.number || ""
                      }
                      onChange={(event) => {
                        // Strip all non-digit characters except leading +
                        let raw = event.target.value;
                        const hasPlus = raw.trimStart().startsWith("+");
                        const digits = raw.replace(/\D/g, "");
                        raw = hasPlus ? "+" + digits : digits;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, number: raw, _phoneRaw: raw } : p,
                          ),
                        );
                        if (index === 0) {
                          setPhoneNumberError(raw.trim() === "");
                        }
                      }}
                      onFocus={() => {
                        // Show stripped digits for editing
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) => {
                            if (i !== index) return p;
                            const hasPlus = p.number.trimStart().startsWith("+");
                            const digits = p.number.replace(/\D/g, "");
                            const clean = hasPlus ? "+" + digits : digits;
                            return { ...p, _phoneRaw: clean };
                          }),
                        );
                      }}
                      onBlur={() => {
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) => {
                            if (i !== index) return p;
                            const formatted = formatPhoneNumber(
                              p.number,
                              p.countryCode || "US",
                            );
                            return { ...p, number: formatted, _phoneRaw: undefined };
                          }),
                        );
                      }}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                          "&.MuiInputBase-root fieldset": { py: 0 },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2, md: 1 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Ext"
                      value={phone.ext || ""}
                      onChange={(event) => {
                        const val = event.target.value;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, ext: val } : p,
                          ),
                        );
                      }}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2, md: 2 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      label="Label"
                      placeholder="e.g. Mobile"
                      value={phone.label || ""}
                      onChange={(event) => {
                        const val = event.target.value;
                        setPhoneNumbers((prev) =>
                          prev.map((p, i) =>
                            i === index ? { ...p, label: val } : p,
                          ),
                        );
                      }}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 2, md: 2 }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-end", sm: "center" },
                      gap: 0.25,
                    }}
                  >
                    <Tooltip
                      title={
                        phone.isPrimary ? "Primary phone" : "Set as primary"
                      }
                      // Theme-aware tooltip colors (2026-09-23, per Amine):
                      // the plain default tooltip didn't follow the theme/mode
                      // — same slotProps pattern as the service overlays
                      // (PreAssign / Outreach): paper surface + primary text +
                      // divider border, arrow matched to the bubble.
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            boxShadow: theme.shadows[4],
                            border: `1px solid ${theme.palette.divider}`,
                            fontSize: "0.75rem",
                          },
                        },
                        arrow: {
                          sx: { color: theme.palette.background.paper },
                        },
                      }}
                    >
                      <span>
                        <IconButton
                          disabled={!isEditable}
                          size="small"
                          onClick={() => {
                            setPhoneNumbers((prev) =>
                              prev.map((p, i) => ({
                                ...p,
                                isPrimary: i === index,
                              })),
                            );
                          }}
                        >
                          {phone.isPrimary ? (
                            <StarRoundedIcon
                              fontSize="small"
                              sx={{ color: "warning.main" }}
                            />
                          ) : (
                            <StarBorderRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      disabled={!isEditable || index === 0}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = [...prev];
                          [next[index - 1], next[index]] = [
                            next[index],
                            next[index - 1],
                          ];
                          return next.map((p, i) => ({ ...p, sortOrder: i }));
                        });
                      }}
                    >
                      <ArrowUpwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      disabled={!isEditable || index === phoneNumbers.length - 1}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = [...prev];
                          [next[index + 1], next[index]] = [
                            next[index],
                            next[index + 1],
                          ];
                          return next.map((p, i) => ({ ...p, sortOrder: i }));
                        });
                      }}
                    >
                      <ArrowDownwardRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      disabled={!isEditable || phoneNumbers.length <= 1}
                      size="small"
                      onClick={() => {
                        setPhoneNumbers((prev) => {
                          const next = prev.filter((_, i) => i !== index);
                          if (phone.isPrimary && next.length > 0) {
                            next[0].isPrimary = true;
                          }
                          return next;
                        });
                      }}
                      sx={{
                        "&.Mui-disabled": { opacity: 0.5 },
                      }}
                    >
                      <RemoveRoundedIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid container size={{ xs: 12 }} ref={languagesRef}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" fontWeight="bold">
                  Languages
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  slots={{
                    popper: CustomPopper, // Use the custom Popper component
                  }}
                  multiple
                  size="small"
                  id="checkboxes-tags-demo"
                  options={overlayData.Languages}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.Name}
                  //getoptionselected={(option, value) => option.id === value.id} // Add this line
                  value={selectedLanguages}
                  onChange={(event, newValue) => {
                    const occurrenceMap = newValue.reduce((acc, item) => {
                      acc[item.id] = (acc[item.id] || 0) + 1;
                      return acc;
                    }, {});

                    const uniqueValues = newValue.filter(
                      (item) => occurrenceMap[item.id] === 1,
                    );

                    // Keep existing entries untouched; newly added languages
                    // default to capability ON with the simult field attached
                    // to the base (legacy-compatible 'All' default).
                    const updatedValues = uniqueValues.map((item) => {
                      const existing = selectedLanguages.find(
                        (lang) => lang.id === item.id,
                      );
                      if (existing) return existing;

                      const seededRate = item.Rate ?? (rate || "");
                      return {
                        ...item,
                        Rate: seededRate,
                        CanHandleSimultaneous: true,
                        SimultaneousTouched: false,
                        SimultaneousRate: seededRate,
                      };
                    });

                    setSelectedLanguages(updatedValues);
                  }}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isSelected = selectedLanguages.some(
                      (lang) => lang.id === option.id,
                    ); // Check if the option is selected
                    return (
                      <li key={key} {...optionProps}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={isSelected}
                          //onDelete={(event) => {
                          //  event.stopPropagation();
                          //  handleDelete(option);
                          //}}
                        />
                        {option.Name}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Languages"
                      placeholder="Type or select from the list"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" fontWeight="bold">
                  Dialects
                </Typography>
              </Grid>
              {selectedLanguages.map((language) => {
                const languageDialects = allDialects.filter(
                  (d) => String(d.LanguageID) === String(language.id),
                );
                const langSelectedDialects = selectedDialects.filter(
                  (d) => String(d.LanguageID) === String(language.id),
                );
                if (languageDialects.length === 0) return null;
                return (
                  <Grid
                    size={{ xs: 12 }}
                    key={`dialects-${language.id}`}
                    //sx={{ mt: 1 }}
                  >
                    <Autocomplete
                      slots={{ popper: CustomPopper }}
                      multiple
                      size="small"
                      disableCloseOnSelect
                      options={languageDialects}
                      getOptionLabel={(option) => option.Name || ""}
                      value={langSelectedDialects}
                      onChange={(_, newValue) => {
                        setSelectedDialects((prev) => {
                          // Remove all dialects for this language, then add the new selection
                          const otherDialects = prev.filter(
                            (d) => String(d.LanguageID) !== String(language.id),
                          );
                          return [...otherDialects, ...newValue];
                        });
                      }}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value?.id)
                      }
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;
                        return (
                          <li key={key} {...optionProps}>
                            <Checkbox
                              icon={icon}
                              checkedIcon={checkedIcon}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.Name}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${language.Name} Dialects`}
                          placeholder="Type or select dialects"
                        />
                      )}
                    />
                  </Grid>
                );
              })}
              {selectedLanguages.length > 0 &&
                selectedLanguages.every(
                  (language) =>
                    allDialects.filter(
                      (d) => String(d.LanguageID) === String(language.id),
                    ).length === 0,
                ) && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      No dialects available for the selected languages.
                    </Typography>
                  </Grid>
                )}
              {selectedLanguages.length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Select languages above to see available dialects.
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" fontWeight="bold">
                  Rates
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      checked={!useMultipleRates}
                      onChange={() => {
                        setUseMultipleRates(!useMultipleRates);

                        // Flip modes: copy the shared BASE rate into every
                        // language only. Capability flags and detached simult
                        // values are preserved; attached simult fields
                        // re-mirror the base (follow-until-touched).
                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) =>
                            applyBaseRate(lang, rate),
                          ),
                        );
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={
                    useMultipleRates
                      ? "Use a different rate for each language."
                      : "Use a single rate for all languages."
                  }
                  labelPlacement="end"
                />
              </Grid>
            </Grid>

            {!useMultipleRates ? (
              <>
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id="Rate"
                      label="Rate For All Languages"
                      type="number"
                      //defaultValue={rate || "N/A"}
                      error={rateError}
                      value={rate}
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        const newRate =
                          inputValue === "" ? "" : parseFloat(inputValue);
                        const normalizedRate = isNaN(newRate) ? "" : newRate;

                        setRate(normalizedRate);
                        setRateError(
                          inputValue.trim() === "" &&
                            !selectedLanguages.some(hasLanguageRate),
                        );

                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) =>
                            applyBaseRate(lang, normalizedRate),
                          ),
                        );
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputBase-input": {
                            padding: "8.5px 12px !important",
                          },
                          "&.MuiInputBase-root fieldset": {
                            py: 0,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1.5, md: 1 },
                    width: "100%",
                  }}
                >
                  {selectedLanguages.length > 0 && (
                    <Box
                      sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns:
                          "minmax(180px, 1fr) minmax(150px, 170px) minmax(116px, 132px)",
                        columnGap: 1.5,
                        pb: 0.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Language
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title="Can handle Simultaneous assignments"
                        sx={{ fontWeight: 600, cursor: "help", pl: "9px" }}
                      >
                        Simultaneous
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Simult. Rate
                      </Typography>
                    </Box>
                  )}
                  {selectedLanguages.map((language) => {
                    const capabilityCheckbox = (
                      <Checkbox
                        disabled={!isEditable}
                        checked={Boolean(language.CanHandleSimultaneous)}
                        onChange={(event) =>
                          setSelectedLanguages((prevLanguages) =>
                            prevLanguages.map((lang) =>
                              lang.id === language.id
                                ? applySimultaneousCapability(
                                    lang,
                                    event.target.checked,
                                  )
                                : lang,
                            ),
                          )
                        }
                        inputProps={{
                          "aria-label": `Can handle Simultaneous assignments for ${language.Name}`,
                        }}
                        sx={{
                          // Fixed square footprint so the 50% radius hover
                          // highlight is a circle (a stretched grid cell
                          // turned it into an ellipse); start-aligned to
                          // stay under the pl:9px "Simultaneous" header.
                          justifySelf: "start",
                          width: "fit-content",
                        }}
                      />
                    );

                    return (
                      <Box
                        key={`simult-capability-${language.id}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md:
                              "minmax(180px, 1fr) minmax(150px, 170px) minmax(116px, 132px)",
                          },
                          columnGap: 1.5,
                          rowGap: 1,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          title={language.Name}
                          sx={{
                            minWidth: 0,
                            width: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {language.Name}
                        </Typography>
                        {isMdUp ? (
                          capabilityCheckbox
                        ) : (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={capabilityCheckbox}
                            label="Can handle Simultaneous"
                          />
                        )}
                        <TextField
                          disabled={
                            !isEditable || !language.CanHandleSimultaneous
                          }
                          size="small"
                          id={`SimultaneousRate-${language.id}`}
                          label={isMdUp ? undefined : "Simult. Rate"}
                          type="number"
                          value={
                            language.CanHandleSimultaneous
                              ? (language.SimultaneousRate ?? "")
                              : 0
                          }
                          onChange={(event) => {
                            const inputValue = event.target.value;
                            const newRate =
                              inputValue === ""
                                ? ""
                                : parseFloat(inputValue);
                            const normalizedRate = isNaN(newRate)
                              ? ""
                              : newRate;

                            setSelectedLanguages((prevLanguages) =>
                              prevLanguages.map((lang) =>
                                lang.id === language.id
                                  ? {
                                      ...lang,
                                      SimultaneousRate: normalizedRate,
                                      SimultaneousTouched: true,
                                    }
                                  : lang,
                              ),
                            );
                          }}
                          inputProps={{
                            "aria-label": `Simultaneous rate for ${language.Name}`,
                          }}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              "& .MuiInputBase-input": {
                                padding: "8.5px 12px !important",
                              },
                              "&.MuiInputBase-root fieldset": {
                                py: 0,
                              },
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 1.5, md: 1 },
                  width: "100%",
                }}
              >
                {selectedLanguages.length > 0 && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "grid" },
                      gridTemplateColumns:
                        "minmax(180px, 1fr) minmax(96px, 120px) minmax(150px, 170px) minmax(116px, 132px)",
                      columnGap: 1.5,
                      pb: 0.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Language
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Rate
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title="Can handle Simultaneous assignments"
                      sx={{ fontWeight: 600, cursor: "help", pl: "9px" }}
                    >
                      Simultaneous
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Simult. Rate
                    </Typography>
                  </Box>
                )}
                {selectedLanguages.map((language, index) => {
                  const capabilityCheckbox = (
                    <Checkbox
                      disabled={!isEditable}
                      checked={Boolean(language.CanHandleSimultaneous)}
                      onChange={(event) =>
                        setSelectedLanguages((prevLanguages) =>
                          prevLanguages.map((lang) =>
                            lang.id === language.id
                              ? applySimultaneousCapability(
                                  lang,
                                  event.target.checked,
                                )
                              : lang,
                          ),
                        )
                      }
                      inputProps={{
                        "aria-label": `Can handle Simultaneous assignments for ${language.Name}`,
                      }}
                      sx={{
                        // Fixed square footprint so the 50% radius hover
                        // highlight is a circle (a stretched grid cell
                        // turned it into an ellipse); start-aligned to
                        // stay under the pl:9px "Simultaneous" header.
                        justifySelf: "start",
                        width: "fit-content",
                      }}
                    />
                  );

                  return (
                    <Box
                      key={language + "*-*" + index}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md:
                            "minmax(180px, 1fr) minmax(96px, 120px) minmax(150px, 170px) minmax(116px, 132px)",
                        },
                        columnGap: 1.5,
                        rowGap: 1,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        title={language.Name}
                        sx={{
                          minWidth: 0,
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {language.Name}
                      </Typography>
                      <TextField
                        disabled={!isEditable}
                        size="small"
                        id={`Rate-${language.id}`}
                        label={isMdUp ? undefined : "Rate"}
                        type="number"
                        value={language.Rate}
                        onChange={(event) => {
                          const inputValue = event.target.value;
                          const newRate =
                            inputValue === "" ? "" : parseFloat(inputValue);
                          const normalizedRate = isNaN(newRate)
                            ? ""
                            : newRate;
                          setSelectedLanguages((prevLanguages) =>
                            prevLanguages.map((lang) =>
                              lang.id === language.id
                                ? applyBaseRate(lang, normalizedRate)
                                : lang,
                            ),
                          );
                        }}
                        inputProps={{
                          "aria-label": `Rate for ${language.Name}`,
                        }}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "&.MuiInputBase-root fieldset": {
                              py: 0,
                            },
                          },
                        }}
                      />
                      {isMdUp ? (
                        capabilityCheckbox
                      ) : (
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={capabilityCheckbox}
                          label="Can handle Simultaneous"
                        />
                      )}
                      <TextField
                        disabled={
                          !isEditable || !language.CanHandleSimultaneous
                        }
                        size="small"
                        id={`SimultaneousRate-${language.id}`}
                        label={isMdUp ? undefined : "Simult. Rate"}
                        type="number"
                        value={
                          language.CanHandleSimultaneous
                            ? (language.SimultaneousRate ?? "")
                            : 0
                        }
                        onChange={(event) => {
                          const inputValue = event.target.value;
                          const newRate =
                            inputValue === "" ? "" : parseFloat(inputValue);
                          const normalizedRate = isNaN(newRate)
                            ? ""
                            : newRate;
                          setSelectedLanguages((prevLanguages) =>
                            prevLanguages.map((lang) =>
                              lang.id === language.id
                                ? {
                                    ...lang,
                                    SimultaneousRate: normalizedRate,
                                    SimultaneousTouched: true,
                                  }
                                : lang,
                            ),
                          );
                        }}
                        inputProps={{
                          "aria-label": `Simultaneous rate for ${language.Name}`,
                        }}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "&.MuiInputBase-root fieldset": {
                              py: 0,
                            },
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Service Types
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <List
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  padding: 0,
                  overflowX: "auto",
                  height: "100%",
                  width: "100%",
                }}
              >
                {availableTypes.map((availableType) => {
                  const labelId = `checkbox-list-label-${availableType}`;

                  return (
                    <ListItem key={availableType.Name} disablePadding>
                      <ListItemButton
                        role={undefined}
                        onClick={() =>
                          updateAvailability(
                            availableType.Name,
                            !availableType.IsAvailable,
                          )
                        }
                        dense
                        disabled={!isEditable}
                      >
                        <ListItemIcon>
                          <Checkbox
                            disabled={!isEditable}
                            edge="start"
                            checked={availableType.IsAvailable}
                            tabIndex={-1}
                            inputProps={{ "aria-labelledby": labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={labelId}
                          disableTypography
                          primary={
                            <Typography variant="body1">
                              {availableType.Name}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Documents & Requirements
              </Typography>
            </Grid>
            {requirementDefinitions.map((requirementDefinition) => (
              <Grid
                key={requirementDefinition.SourceColumn}
                size={{ xs: 12, md: 6, lg: 4 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <ToggleButtonGroup
                    value={(() => {
                      // If this requirement was just refreshed in this session, show "refresh"
                      if (refreshedRef.current[requirementDefinition.SourceColumn]) return "refresh";
                      return toNullableBoolean(requirementValues[requirementDefinition.SourceColumn]) === true ? "on" : "off";
                    })()}
                    exclusive
                    disabled={!isEditable}
                    onChange={(_, newValue) => {
                      if (newValue === null) return;
                      if (newValue === "refresh") {
                        // Clear the refreshed flag so the switch can move to refresh position
                        handleMarkUpdated(requirementDefinition.SourceColumn);
                      } else {
                        // Clear the refreshed flag when user toggles back to on/off
                        delete refreshedRef.current[requirementDefinition.SourceColumn];
                        handleRequirementToggle(
                          requirementDefinition.SourceColumn,
                          newValue === "on",
                        );
                      }
                    }}
                    size="small"
                    sx={{
                      backgroundColor: "background.default",
                      borderRadius: "12px",
                      p: 0.5,
                      gap: 0.75,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0px 4px 10px rgba(0,0,0,0.08)",
                      border: "1px solid",
                      borderColor: "primary.border2",
                      "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "10px !important",
                        color: "primary.main",
                        textTransform: "none",
                        fontWeight: 700,
                        px: 1.5,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "primary.buttonHover",
                          color: "primary.main",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "background.default",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12), 0px 4px 10px rgba(0,0,0,0.12)",
                          "&:hover": {
                            backgroundColor: "primary.buttonHoverSelected",
                            color: "background.default",
                          },
                        },
                        "&.Mui-disabled": {
                          color: "text.disabled",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="off">OFF</ToggleButton>
                    <ToggleButton value="on">ON</ToggleButton>
                    <HtmlTooltip
                      title={
                        toNullableBoolean(requirementValues[requirementDefinition.SourceColumn]) === true
                          ? "Mark as updated today"
                          : "Enable requirement first to mark as updated"
                      }
                      placement="top"
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        <ToggleButton
                          value="refresh"
                          disabled={!isEditable || toNullableBoolean(requirementValues[requirementDefinition.SourceColumn]) !== true}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1,
                            "&.Mui-selected": {
                              backgroundColor: "warning.main",
                              color: "#fff",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.12), 0px 4px 10px rgba(0,0,0,0.12)",
                              "&:hover": {
                                backgroundColor: "warning.dark",
                                color: "#fff",
                              },
                            },
                          }}
                        >
                          <RefreshRoundedIcon sx={{ fontSize: "0.9rem", display: "block" }} />
                        </ToggleButton>
                      </span>
                    </HtmlTooltip>
                  </ToggleButtonGroup>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      fontSize: "0.8rem",
                    }}
                  >
                    {requirementDefinition.Name}
                  </Typography>
                </Box>
              </Grid>
            ))}
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                //select
                id="Certification"
                label="Certification"
                value={certification}
                onFocus={(event) => {
                  if (!certification) {
                    setCertification("");
                  }
                }}
                onBlur={(event) => {
                  if (!event.target.value || event.target.value.trim() === "") {
                    setCertification("N/A");
                  }
                }}
                onChange={(event) => {
                  const newValue = event.target.value;
                  setCertification(newValue);

                  if (newValue === "Other Certification") {
                    setTimeout(() => {
                      otherCertificationRef.current.scrollIntoView({
                        behavior: "smooth",
                      });
                    }, 250);
                  } else {
                    setCertificationOther("");
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              ></TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" fontWeight="bold">
                Portal Notification Settings
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Browser Notifications
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.25 }}>
                {notificationSettings.BrowserNotificationsEnabled
                  ? "Enabled"
                  : "Disabled"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                SMS Notifications
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.25 }}>
                {notificationSettings.SmsNotificationsEnabled
                  ? "Enabled"
                  : "Disabled"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Quiet Hours
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.25 }}>
                {notificationSettings.QuietHoursStart || "22:00"} -{" "}
                {notificationSettings.QuietHoursEnd || "07:00"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Timezone
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                <TextField
                  select
                  size="small"
                  value={selectedTimezone}
                  onChange={(event) => {
                    setLinguistTimezone(event.target.value);
                  }}
                  sx={{ minWidth: 190, flex: 1 }}
                >
                  {timezoneOptions.map((timezoneOption) => (
                    <MenuItem
                      key={timezoneOption.value}
                      value={timezoneOption.value}
                    >
                      {timezoneOption.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Grid>
          </Grid>
        </Box>
    </Overlay>
  );
};

export default LinguistNewEdit;
